import {
  type ReportDateRangeFilter,
  ReportDateRangePreset,
} from 'src/api/generated';
import type { TranslateParams } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/**
 * Translation function used by report date-range validation helpers.
 */
export type ReportDateRangeTranslator = (
  key: TranslationKey,
  params?: TranslateParams
) => string;

/**
 * Returns the user-facing validation issue for one report date range, if any.
 */
export const getReportDateRangeValidationMessage = (
  value: ReportDateRangeFilter,
  t: ReportDateRangeTranslator
): string | null => {
  if (value.preset !== ReportDateRangePreset.CUSTOM_RANGE) {
    return null;
  }

  if (!value.from || !value.to) {
    return t('reports.dashboard.dateRange.errors.missing');
  }

  if (!isCalendarDateToken(value.from) || !isCalendarDateToken(value.to)) {
    return t('reports.dashboard.dateRange.errors.invalid');
  }

  if (value.from > value.to) {
    return t('reports.dashboard.dateRange.errors.order');
  }

  return null;
};

/**
 * Returns whether the report date range can be queried safely.
 */
export const isReportDateRangeValid = (
  value: ReportDateRangeFilter,
  t: ReportDateRangeTranslator
): boolean => getReportDateRangeValidationMessage(value, t) === null;

const isCalendarDateToken = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const [rawYear, rawMonth, rawDay] = value.split('-');
  const year = Number(rawYear);
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  return (
    utcDate.getUTCFullYear() === year &&
    utcDate.getUTCMonth() === month - 1 &&
    utcDate.getUTCDate() === day
  );
};
